import { steps } from '../../steps-library/step-util/steps-list';
import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Location } from '@angular/common';
import * as moment from 'moment-timezone';
import { NgxSmartModalService } from 'ngx-smart-modal';
import {
  AlertService,
  CampaignService,
  FunnelService,
  FunnelStepService,
  LoaderService,
  PusherService,
  StepTemplateService,
  UserService,
} from '../../_services';
import { CrudPagedListComponent } from '../../_directives';
import {
  Pager,
  OfferIntents,
  OfferTypes,
  StepTableStatusLabels,
  StepPopupActions,
  StepTableStatuses,
  StepsSortConfig,
  TableSortDirection,
  StepSortableColumns,
  StepHeaderDetails,
  StepTypePopupData,
  OfferTypeLabels,
  StepCategory,
  CustomStepCategoryEnum,
  User,
  Campaign,
  NotificationType,
  OfferStages,
} from '../../_models';
import { PopupData, SurveyPopupTitleMap, SurveyStep } from './survey';
import { RetentionOffer } from '../retention/retention';

@Component({
  moduleId: module.id.toString(),
  selector: 'survey-steps-component',
  templateUrl: './survey-steps.component.html',
  styleUrls: ['./survey-steps.component.scss'],
})
export class SurveyStepsComponent extends CrudPagedListComponent implements OnInit {
  campaign: Campaign = null;
  tableStatuses = StepTableStatuses;
  tableData: (SurveyStep | RetentionOffer)[] = [];
  originalTableData: (SurveyStep | RetentionOffer)[] = [...this.tableData];
  offerType: OfferTypes = OfferTypes.Survey;
  isLoading = false;
  isAdminView: boolean = false;
  user: User;
  stepPopupActions = StepPopupActions;
  offerIntents = OfferIntents;
  stepStatusLabel = StepTableStatusLabels;
  stepHeaderDetails = StepHeaderDetails;
  currentSort: StepsSortConfig = {
    column: null,
    direction: null,
  };
  columnDirections = TableSortDirection;
  sortableColumns = StepSortableColumns;
  popupTitle: string = '';
  popupData: PopupData = {
    name: '',
    is_survey: false,
    offer_id: null,
    step_id: null,
    offer_intent: null,
    offer_type: null,
    popupType: null,
  };
  stepTypePopupData: StepTypePopupData = {
    is_survey: false,
    offer_name: '',
    offer_id: null,
    offer_intent: null,
    offer_type: null,
    popupType: null,
    steps: [],
    use_existing_template: false,
  };
  public surveyCategories: StepCategory[] = [
    CustomStepCategoryEnum.Survey,
    CustomStepCategoryEnum.CancelOrderSurvey,
    CustomStepCategoryEnum.CancelTrialSurvey,
    CustomStepCategoryEnum.CancelSubSurvey,
    CustomStepCategoryEnum.ReturnSurvey,
    CustomStepCategoryEnum.ResolveSurvey,
  ];

  constructor(
    protected router: Router,
    protected location: Location,
    protected alertService: AlertService,
    protected funnelService: FunnelService,
    protected stepTemplateService: StepTemplateService,
    protected campaignService: CampaignService,
    protected userService: UserService,
    protected loader: LoaderService,
    public ngxSmartModalService: NgxSmartModalService,
    protected route: ActivatedRoute,
    protected pusherService: PusherService,
    protected funnelStepService: FunnelStepService
  ) {
    super(router, location, route, funnelService, alertService);
    this.objectName = 'surveys';
  }

  ngOnInit() {
    this.loader.show();

    this.isAdminView = this.route.snapshot.data['isAdminView'];
    this.campaign = this.campaignService.getCurrentCampaign();

    this.userService.getCurrent().subscribe((user: User) => {
      this.user = user;
    });

    if (this.pusherService.channel) {
      this.pusherService.channel.bind(NotificationType[NotificationType.PUBLISH_SURVEY_TEMPLATE], (data: any) => {
        data = data.payload;
        if (data && data.is_completed) {
          this.fetchData();
        }
      });
    }

    this.fetchData();
  }

  fetchData() {
    if (this.isAdmin) {
      this.fetchSurveyData();
    } else {
      this.fetchOffersList();
    }
  }

  fetchSurveyData() {
    this.stepTemplateService.list({ is_template: false, category__in: this.surveyCategories.join(',') }).subscribe(
      (response) => {
        this.loader.hide();
        this.tableData = response.results.map((item: SurveyStep) => {
          const lastModifiedMoment = moment(item.last_modified).tz('EST');

          const lastModified = {
            time: lastModifiedMoment.format('HH:mm:ss z'), // "16:18:10 EST"
            date: lastModifiedMoment.format('DD MMM, YYYY'), // "20 Jan, 2025"
          };

          return {
            id: item.id,
            displayId: item.display_id,
            stepName: item.name,
            status: item.is_public ? StepTableStatuses.Active : StepTableStatuses.Inactive,
            type: item.category,
            offerIntent: item.offer_intent || null,
            lastModified,
            sessions: item.sessions || null,
            stage: item.offer_intent ? OfferStages[item.offer_intent] : null,
            category: item.category,
            showThreeDotMenu: false,
            previewLoading: false,
            isPublic: false,
            isPublishing: false,
          };
        });
        this.originalTableData = this.tableData;
      },
      (error) => {
        this.loader.hide();
        this.handleError(error);
      }
    );
  }

  fetchOffersList() {
    this.funnelService
      .listRetentionOffers(this.campaign.id, null, OfferTypes.Survey, true)
      .subscribe((response: Pager) => {
        if (response.results && response.results.length > 0) {
          this.tableData = response.results.map((item: any) => {
            const lastModifiedMoment = moment(item.updated).tz('EST');

            const lastModified = {
              time: lastModifiedMoment.format('HH:mm:ss z'), // "16:18:10 EST"
              date: lastModifiedMoment.format('DD MMM, YYYY'), // "20 Jan, 2025"
            };

            return {
              id: item.id,
              displayId: item.display_id,
              stepName: item.name,
              status: item.is_active ? StepTableStatuses.Active : StepTableStatuses.Inactive,
              type: item.category || null,
              lastModified,
              offerIntent: item.offer_intent || null,
              sessions: item.sessions || null,
              stage: item.offer_intent ? OfferStages[item.offer_intent] : null,
              category: item.category || null,
              firstStep: item.first_step || null,
              showThreeDotMenu: false,
              previewLoading: false,
              isPublishable: item.is_modified,
              isPublishing: false,
            };
          });
          this.originalTableData = this.tableData;
        }
        this.loader.hide();
      });
  }

  openStepPopup(
    popupType: StepPopupActions,
    stepId?: number | string,
    stepName?: string,
    offerIntent?: OfferIntents
  ): void {
    if (this.isAdmin) {
      this.popupTitle = SurveyPopupTitleMap[popupType] || '';

      this.popupData = {
        ...this.popupData,
        name: stepName || '',
        step_id: stepId || null,
        is_survey: true,
        popupType: popupType,
        offer_intent: offerIntent || null,
      };

      this.ngxSmartModalService.getModal('stepPopup').open();
    } else {
      this.openUserStepPopup(popupType, stepId, stepName, offerIntent);
    }
  }

  saveStep(): void {
    this.fetchSurveyData();
    this.ngxSmartModalService.getModal('stepPopup').close();
  }

  closeStepPopup() {
    this.ngxSmartModalService.getModal('stepPopup').close();
  }

  openUserStepPopup(
    popupType: StepPopupActions,
    offerId?: number | string,
    stepName?: string,
    offerIntent?: OfferIntents
  ): void {
    this.popupTitle = SurveyPopupTitleMap[popupType] || '';

    this.popupData = {
      ...this.popupData,
      name: stepName || '',
      offer_id: offerId || null,
      is_survey: true,
      popupType: popupType,
      offer_intent: offerIntent || null,
    };

    this.ngxSmartModalService.getModal('userStepPopup').open();
  }

  saveUserStep(): void {
    this.fetchOffersList();
    this.ngxSmartModalService.getModal('userStepPopup').close();
  }

  closeUserStepPopup() {
    this.ngxSmartModalService.getModal('userStepPopup').close();
  }

  openSelectTypePopup() {
    this.stepTypePopupData = {
      ...this.stepTypePopupData,
      offer_name: OfferTypeLabels[this.offerType],
      offer_id: this.popupData.offer_id,
      offer_intent: this.popupData.offer_intent,
      offer_type: this.offerType,
      is_survey: true,
      use_existing_template: false,
      popupType: StepPopupActions.SelectType,
      steps: this.filterSteps(this.offerType, this.popupData.offer_intent) || [],
    };

    this.ngxSmartModalService.getModal('stepTypePopup').open();
  }

  closeStepTypePopup() {
    this.ngxSmartModalService.getModal('stepTypePopup').close();
  }

  saveStepType(event: { path: string[] }) {
    this.closeStepTypePopup();
    this.router.navigate(event.path);
  }

  navigateToStepBuilder(id: number, category: number, firstStep: any = null, offerIntent: OfferIntents = null) {
    if (this.isAdmin && offerIntent) {
      this.router.navigate(['intent', offerIntent, 'category', this.offerType, category, 'step', id], {
        relativeTo: this.route,
      });
    } else if (firstStep) {
      this.router.navigate(['stage', id, 'category', OfferTypes.Survey, firstStep.category, 'step', firstStep.id], {
        relativeTo: this.route,
      });
    }
  }

  filterSteps(offerType: OfferTypes, offerIntent: OfferIntents): any[] {
    return steps.filter(
      (step) =>
        step.offer_type === offerType &&
        step.offer_intent &&
        (!step.offer_intents || step.offer_intents.length === 0 || step.offer_intents.includes(offerIntent))
    );
  }

  sortByName() {
    if (this.currentSort.column === StepSortableColumns.Name) {
      this.currentSort.direction =
        this.currentSort.direction === TableSortDirection.Asc ? TableSortDirection.Desc : TableSortDirection.Asc;
    } else {
      this.currentSort.column = StepSortableColumns.Name;
      this.currentSort.direction = TableSortDirection.Asc;
    }

    this.tableData.sort((a: any, b: any) => {
      const comparison = a.stepName.localeCompare(b.stepName);
      return this.currentSort.direction === TableSortDirection.Asc ? comparison : -comparison;
    });
  }

  sortByLastModified() {
    if (this.currentSort.column === StepSortableColumns.LastModified) {
      this.currentSort.direction =
        this.currentSort.direction === TableSortDirection.Asc ? TableSortDirection.Desc : TableSortDirection.Asc;
    } else {
      this.currentSort.column = StepSortableColumns.LastModified;
      this.currentSort.direction = TableSortDirection.Asc;
    }

    this.tableData.sort((a: any, b: any) => {
      const dateA = new Date(`${a.lastModified.date} ${a.lastModified.time}`);
      const dateB = new Date(`${b.lastModified.date} ${b.lastModified.time}`);
      return this.currentSort.direction === TableSortDirection.Asc
        ? dateA.getTime() - dateB.getTime()
        : dateB.getTime() - dateA.getTime();
    });
  }

  getSurveyStepPreview(id: string | number) {
    const step = this.tableData.find((item) => item.id === id);
    step.previewLoading = true;

    const onSuccess = (data: any) => {
      window.open(data.toString(), '_blank');
      step.previewLoading = false;
    };

    const onError = (error: any) => {
      this.handleError(error);
      step.previewLoading = false;
    };

    if (this.isAdmin) {
      this.stepTemplateService.get(id).subscribe((resp: any) => {
        if (resp) {
          const stepDetails = {
            ...resp,
            static_data: {
              ...resp.static_data,
              image: resp.static_data.image ? resp.static_data.image.id : null,
              image_mobile: resp.static_data.image_mobile ? resp.static_data.image_mobile.id : null,
              image_tablet: resp.static_data.image_tablet ? resp.static_data.image_tablet.id : null,
            },
          };

          this.funnelService.previewStep(this.campaign.id, stepDetails).subscribe(onSuccess, onError);
        } else {
          step.previewLoading = false;
        }
      });
    } else if (step && 'firstStep' in step && step.firstStep.id) {
      this.funnelStepService.setFunnelId(step.id);
      this.funnelStepService.generatePreview(step.firstStep.id, this.campaign.id).subscribe(onSuccess, onError);
    } else {
      this.funnelService.preview(this.campaign.id, step).subscribe(onSuccess, onError);
    }
  }

  publishAdminSurvey(item): void {
    item.isPublishing = true;
    item.isPublic = true;

    this.stepTemplateService.publish(item.id).subscribe(
      () => {
        item.isPublishing = false;
        this.alertService.success('Survey changes are being published.');
      },
      (error) => {
        this.handleError(error);
        this.loader.hide();
      }
    );
  }

  publish(item): void {
    item.isPublishing = true;

    this.funnelService.publish(item.id).subscribe(
      () => {
        item.isPublishable = false;
        item.isPublishing = false;
        this.alertService.success('Survey changes are being published.');
      },
      (error) => {
        this.handleError(error);
        item.isPublishing = false;
      }
    );
  }

  closeOtherMenus(currentItem: SurveyStep): void {
    this.tableData.forEach((item) => {
      if (item.id !== currentItem.id) {
        item.showThreeDotMenu = false;
      }
    });
  }

  toggleThreeDotMenu(item: SurveyStep): void {
    this.closeOtherMenus(item);
    item.showThreeDotMenu = !item.showThreeDotMenu;
  }

  closeThreeDotMenu(item: SurveyStep): void {
    item.showThreeDotMenu = false;
  }

  closeAllMenus(): void {
    this.tableData.forEach((item) => {
      item.showThreeDotMenu = false;
    });
  }

  isNameSorted(): boolean {
    return this.currentSort.column === StepSortableColumns.Name;
  }

  get isNameAsc(): boolean {
    return this.isNameSorted() && this.currentSort.direction === TableSortDirection.Asc;
  }

  get isNameDesc(): boolean {
    return this.isNameSorted() && this.currentSort.direction === TableSortDirection.Desc;
  }

  isDateSorted(): boolean {
    return this.currentSort.column === StepSortableColumns.LastModified;
  }

  get isDateAsc(): boolean {
    return this.isDateSorted() && this.currentSort.direction === TableSortDirection.Asc;
  }

  get isAdmin(): boolean {
    return this.isAdminView && this.user && this.user.is_staff;
  }

  get isDateDesc(): boolean {
    return this.isDateSorted() && this.currentSort.direction === TableSortDirection.Desc;
  }

  isOfferActive(status: StepTableStatuses): boolean {
    return status === StepTableStatuses.Active;
  }
}
